import {
  lostBonusOfferDescription,
  lostBonusOfferTitle,
  lostBonusProduct,
  lostBonusProductLabel,
  type LostBonusOffer,
} from '@/domain/lost-bonuses';
import { cn } from '@/lib/cn';
import { Badge, Icon, type IconName } from '@/ui/common';

export interface ClaimBonusOfferCardProps {
  offer: LostBonusOffer;
  selected: boolean;
  onSelect: () => void;
  label: (key: string, fallback: string) => string;
  className?: string;
}

/**
 * Selectable lost-bonus offer card (Figma ClaimBonusOfferCard — Sports | Casino).
 */
export function ClaimBonusOfferCard({
  offer,
  selected,
  onSelect,
  label,
  className,
}: ClaimBonusOfferCardProps) {
  const title =
    lostBonusOfferTitle(offer) || label('ClaimBonus', 'Claim bonus');
  const description = lostBonusOfferDescription(
    offer,
    label(
      'Pick one eligible offer below. Rewards credit after you confirm.',
      'Pick one eligible offer below. Rewards credit after you confirm.',
    ),
  );
  const product = lostBonusProduct(offer.ModuleId);
  const isCasino = product === 'casino';
  const productLabel = lostBonusProductLabel(product, label);
  const productIcon: IconName = isCasino ? 'casino' : 'football';
  const accentClass = isCasino ? 'bg-status-warning' : 'bg-action-primary';
  const glowClass = isCasino
    ? 'from-status-warning/20 via-status-warning/10 to-transparent'
    : 'from-action-primary/20 via-action-primary/10 to-transparent';
  const selectedBorder = isCasino
    ? 'border-2 border-status-warning'
    : 'border-2 border-action-primary';
  const selectedRadio = isCasino
    ? 'border-status-warning bg-status-warning'
    : 'border-action-primary bg-action-primary';

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={title}
      onClick={onSelect}
      className={cn(
        'flex w-full flex-col items-stretch overflow-hidden rounded-md border bg-bg-surface text-left transition-colors',
        selected
          ? selectedBorder
          : 'border border-border-default hover:border-action-primary/60',
        className,
      )}
    >
      <div className="relative flex items-center gap-3 overflow-hidden border-b border-border-default bg-bg-elevated px-4 py-[18px]">
        <div
          className={cn(
            'pointer-events-none absolute inset-0 bg-gradient-to-t',
            glowClass,
          )}
          aria-hidden
        />
        <span
          className={cn('relative h-11 w-1 shrink-0 rounded-sm', accentClass)}
          aria-hidden
        />
        <div className="relative flex min-w-0 flex-1 flex-col items-start gap-1.5">
          <Badge tone={isCasino ? 'warning' : 'info'} size="sm">
            {productLabel}
          </Badge>
          <span className="text-[11px] font-bold tracking-wide text-text-secondary uppercase">
            {label('LOSS BONUS', 'LOSS BONUS')}
          </span>
        </div>
        <Icon
          name={productIcon}
          size={28}
          className="relative shrink-0 text-text-secondary"
        />
        <span
          className={cn(
            'relative flex size-[22px] shrink-0 items-center justify-center rounded-full border-2',
            selected ? selectedRadio : 'border-border-default bg-transparent',
          )}
          aria-hidden
        >
          {selected ? (
            <span className="size-2.5 rounded-full bg-text-on-action" />
          ) : null}
        </span>
      </div>
      <div className="flex flex-col gap-1.5 px-4 pt-3.5 pb-4">
        <p className="text-[16px] font-bold text-text-primary">{title}</p>
        {description ? (
          <p className="text-[13px] text-text-secondary">{description}</p>
        ) : null}
        <p className="text-[11px] text-text-secondary">
          {label('One claim per period · T&Cs apply', 'One claim per period · T&Cs apply')}
        </p>
      </div>
    </button>
  );
}
